import {
	Content,
	InvestmentContentWrapper,
	InvestmentHeader,
	Layout,
	MainLayout,
} from '@dealstack/frontend/investor/pages/Investment/common/investmentLayout';
import { INVESTMENT_PORTAL } from '@dealstack/frontend/investor/pages/Investment/common/renderHeaderPortal';
import {
	INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES,
	InvestmentLeaveChecksComplete,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentLeave/InvestmentLeaveChecksComplete/InvestmentLeaveChecksComplete';
import { InvestmentLeaveCompleted } from '@dealstack/frontend/investor/pages/Investment/InvestmentLeave/InvestmentLeaveCompleted';
import { InvestmentLeaveContracts } from '@dealstack/frontend/investor/pages/Investment/InvestmentLeave/InvestmentLeaveContracts/InvestmentLeaveContracts';
import {
	INVESTMENT_LEAVE_PERSONAL_DETAILS_ROUTES,
	InvestmentLeavePersonalDetails,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentLeave/InvestmentLeavePersonalDetails/InvestmentLeavePersonalDetails';
import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { PrimarySideListV2 } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	getInvestmentDetails,
	updateInvestorCompletionStep,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import { Title } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	INVESTMENT_ROUTES,
	investmentFlowPath,
} from '@dealstack/frontend/investor/pages/Investment/Investment.routes';
import { MainHeaderBar } from '@dealstack/frontend/investor/pages/Investment/common/MainHeaderBar';

import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import {
	InvestmentWorkflowCompletionStep,
	PatchShareholderInvestorCompletionStepDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { isNumber } from 'lodash';
import {
	NoSaveStep,
	useWizardStepRegistry,
	WizardStepRegistryProvider,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardNavigationIntent } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';

import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import { useDelay } from '@dealstack/frontend/investor/pages/Investment/hooks/useDelay';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { useInvestorCustomDetails } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestorCustomDetails';
import { useBackupController } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { useInvestmentJoinAndAllocationController } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { PrimarySideListV2Props } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum INVESTMENT_LEAVE_ROUTES {
	PERSONAL_DETAILS = `/personal-details`,
	CHECKS_COMPLETE = `/complete-checks`,
	CONTRACTS = `/contracts`,
	COMPLETED = '/completed',
}

interface SidebarConfigItem {
	key: number;
	title: string;
	route: string;
	id?: PatchShareholderInvestorCompletionStepDto['step'] | 'CHECKS_COMPLETE';
	subitems?: {
		key: number;
		title: string;
		route: string;
		id: PatchShareholderInvestorCompletionStepDto['step'];
	}[];
}

type InvestmentLeaveSidebarConfig = SidebarConfigItem[];

const PERSONAL_INFORMATION_STEP_KEY = 1;
const COMPANY_INFORMATION_STEP_KEY = 2;
const BANK_ACCOUNT_STEP_KEY = 3;
const PERSONAL_IDS_STEP_KEY = 6;
const INVESTMENT_VEHICLES_STEP_KEY = 7;
const FORMS_STEP_KEY = 8;
const DECLARATIONS_STEP_KEY = 9;
const DOCUMENTS_STEP_KEY = 10;

const getSideBarConfig = (
	t: TranslateFunction<I18Namespaces.INVESTOR>
): InvestmentLeaveSidebarConfig => [
	{
		title: t('investment.join.sidebar.steps.personal_details'),
		route: INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS,
		key: 0,
		subitems: [
			{
				title: t('investment.basic_info'),
				route: `${INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS}${INVESTMENT_LEAVE_PERSONAL_DETAILS_ROUTES.PERSONAL_INFORMATION}`,
				id: InvestmentWorkflowCompletionStep.PERSONAL_INFORMATION,
				key: PERSONAL_INFORMATION_STEP_KEY,
			},
			{
				title: t('investment.investment_vehicle'),
				route: `${INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS}${INVESTMENT_LEAVE_PERSONAL_DETAILS_ROUTES.INVESTMENT_VEHICLE_INFORMATION}`,
				id: InvestmentWorkflowCompletionStep.INVESTMENT_VEHICLE_INFORMATION,
				key: COMPANY_INFORMATION_STEP_KEY,
			},
			{
				title: t('investment.join.sidebar.steps.bank_account'),
				route: `${INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS}${INVESTMENT_LEAVE_PERSONAL_DETAILS_ROUTES.BANK_ACCOUNT}`,
				id: InvestmentWorkflowCompletionStep.BANK_ACCOUNT,
				key: BANK_ACCOUNT_STEP_KEY,
			},
		],
	},
	{
		title: t('investment.join.sidebar.steps.complete_checks'),
		route: INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE,
		id: 'CHECKS_COMPLETE' as PatchShareholderInvestorCompletionStepDto['step'],
		key: 4,
		subitems: [
			{
				title: t('investment.instructions'),
				route: `${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}${INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES.INSTRUCTIONS}`,
				id: InvestmentWorkflowCompletionStep.KYC_INSTRUCTIONS,
				key: 5,
			},
			{
				title: t('investment.personal_ids'),
				route: `${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}${INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES.PERSONAL}`,
				id: InvestmentWorkflowCompletionStep.KYC_PERSONAL,
				key: PERSONAL_IDS_STEP_KEY,
			},
			{
				title: t('investment.company_ids'),
				route: `${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}${INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES.COMPANY}`,
				id: InvestmentWorkflowCompletionStep.KYC_COMPANY,
				key: INVESTMENT_VEHICLES_STEP_KEY,
			},
			{
				title: t('investment.join.sidebar.steps.forms'),
				route: `${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}${INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES.KYC_FORMS}`,
				id: InvestmentWorkflowCompletionStep.KYC_FORMS,
				key: FORMS_STEP_KEY,
			},
			{
				title: t('investment.join.sidebar.steps.declarations'),
				route: `${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}${INVESTMENT_LEAVE_CHECKS_COMPLETE_ROUTES.DECLARATIONS}`,
				id: InvestmentWorkflowCompletionStep.DECLARATIONS,
				key: DECLARATIONS_STEP_KEY,
			},
		],
	},
	{
		title: t('investment.join.sidebar.steps.contracts'),
		route: INVESTMENT_LEAVE_ROUTES.CONTRACTS,
		id: InvestmentWorkflowCompletionStep.CONTRACTS,
		key: DOCUMENTS_STEP_KEY,
	},
	{
		title: t('investment.join.sidebar.steps.application_completed'),
		route: INVESTMENT_LEAVE_ROUTES.COMPLETED,
		id: InvestmentWorkflowCompletionStep.COMPLETED,
		key: 11,
	},
];

const SIDEBAR_MAX_ITEMS = 11;

export const InvestmentLeave = () => {
	const [isContinueDisabled, setIsContinueDisabled] = useState(false);
	const { dealId, mipId, investmentWorkflowId } = useParams();
	const leaveFlowPath = investmentFlowPath(INVESTMENT_ROUTES.LEAVE, {
		dealId: dealId ?? '',
		mipId: mipId ?? '',
		investmentWorkflowId: investmentWorkflowId ?? '',
	});

	const getInvestmentDetailsRequest = useQuery({
		queryKey: [
			API_PATHS.SHAREHOLDER_INVESTMENT_BY_MIP_ID_MIPID_GET,
			{ mipId: mipId || '', investmentWorkflowId: investmentWorkflowId || '' },
		],
		queryFn: ({ signal }) =>
			getInvestmentDetails(
				{ mipId: mipId || '', investmentWorkflowId: investmentWorkflowId || '' },
				signal
			),
		enabled: !!mipId && !!investmentWorkflowId,
	});

	const getInvestorDataRequest = useInvestor();
	const updateInvestorCompletionStepRequest = useWrappedRequest(updateInvestorCompletionStep, {
		disableDefaultLocalErrorHandler: true,
	});
	// Custom fields are scoped to this leaver investment's KYC workflow.
	const leaverKycWorkflowId = getInvestmentDetailsRequest.data?.payload?.activeKycWorkflowId;
	const getInvestorCustomDetailsRequest = useInvestorCustomDetails(leaverKycWorkflowId, {
		enabled: !!leaverKycWorkflowId,
	});
	const { pathname } = useLocation();
	const tInvestor = useTranslationInvestor();
	const navigate = useNavigate();
	const { isDelaying, startDelay } = useDelay();
	const stepRegistry = useWizardStepRegistry();

	const [isBackDisabled, setIsBackDisabled] = useState(true);
	const [isLoadingState, setIsLoadingState] = useState(false);
	const { dataBackup, updateBackupData, removeBackupData } = useBackupController();

	const investorId = getInvestorDataRequest?.data?.id;
	const investmentDetails = getInvestmentDetailsRequest.data?.payload;
	const { data: kycSetting, isLoading: isKycSettingLoading } = useKycWorkflowKycSetting(
		investmentDetails?.activeKycWorkflowId ?? ''
	);
	const { data: attachedKycForms } = useGetInvestorKycForms(
		investmentDetails?.activeKycWorkflowId ?? '',
		{ enabled: !!investmentDetails?.activeKycWorkflowId }
	);

	const isPIVInvestment = !!investmentDetails?.isPiv;

	const sideBarConfig = useMemo(() => {
		const config = getSideBarConfig(tInvestor);
		const customDetailsFields = getInvestorCustomDetailsRequest.data?.definitions;
		// kycSetting alone decides whether the bank account step exists, so a config built
		// while it loads hides the step the investor is on — checkInvestorStep then reads
		// that as a settings change and resets them to the first step.
		if (!customDetailsFields || isKycSettingLoading) {
			return null;
		}

		const hideBankAccountTab = isPIVInvestment
			? !kycSetting?.isVehicleBankAccountRequired
			: !kycSetting?.isInvestorBankAccountRequired;

		const isKycRequired = kycSetting?.isKycRequired ?? investmentDetails?.isKycRequired;

		const hideAllKyc = !isKycRequired;
		const hidePIVKyc = !isPIVInvestment;
		const hideDeclarations = !(
			(kycSetting?.isBeneficialOwnershipRequired ?? true) ||
			(kycSetting?.isSourceOfFundsRequired ?? true) ||
			(kycSetting?.isPepRequired ?? true)
		);
		// Hide the step only when this workflow has NO attached forms (respects the per-investment
		// override). While the query loads (undefined), keep it shown.
		const hideKycFormsTab = attachedKycForms !== undefined && attachedKycForms.length === 0;

		const mainStepsToHide: (SidebarConfigItem['id'] | '')[] = [
			hideAllKyc ? 'CHECKS_COMPLETE' : '',
		];
		const subStepsToHide: (SidebarConfigItem['id'] | '')[] = [
			hideBankAccountTab ? InvestmentWorkflowCompletionStep.BANK_ACCOUNT : '',
			hidePIVKyc ? InvestmentWorkflowCompletionStep.KYC_COMPANY : '',
			hideKycFormsTab ? InvestmentWorkflowCompletionStep.KYC_FORMS : '',
			hideDeclarations ? InvestmentWorkflowCompletionStep.DECLARATIONS : '',
			!isPIVInvestment ? InvestmentWorkflowCompletionStep.INVESTMENT_VEHICLE_INFORMATION : '',
		];

		const configWithFilteredMainSteps = config.filter(
			(step) => !step.id || !mainStepsToHide.includes(step.id)
		);

		return configWithFilteredMainSteps.map((step) => {
			if (!step.subitems?.find((subStep) => subStepsToHide.includes(subStep.id))) {
				return step;
			}
			return {
				...step,
				subitems: step.subitems?.filter((subStep) => !subStepsToHide.includes(subStep.id)),
			};
		});
	}, [
		investmentDetails,
		isPIVInvestment,
		getInvestorCustomDetailsRequest.data?.definitions,
		kycSetting,
		isKycSettingLoading,
		attachedKycForms,
	]);

	const activeMainStep = useMemo(
		() => sideBarConfig?.find((step) => pathname.includes(step.route)),
		[pathname, sideBarConfig]
	);

	const activeSubStep = useMemo(
		() => activeMainStep?.subitems?.find((subStep) => pathname.includes(subStep.route)),
		[pathname, activeMainStep?.subitems]
	);

	const activeStepKey = useMemo(
		() => activeSubStep?.key || activeMainStep?.key,
		[activeMainStep, activeSubStep]
	);

	const steps = useMemo(
		() =>
			sideBarConfig?.reduce((acc, current) => {
				return [...acc, current, ...(current.subitems || [])];
			}, [] as InvestmentLeaveSidebarConfig),
		[sideBarConfig]
	);

	const nextStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let nextStepKey = activeStepKey + 1;
		let nextStep;

		while (nextStepKey <= SIDEBAR_MAX_ITEMS) {
			const step: SidebarConfigItem | undefined = steps?.find(
				// eslint-disable-next-line no-loop-func
				(step) => step.key === nextStepKey
			);

			if (step && !step.subitems) {
				nextStep = step;
				break;
			}

			++nextStepKey;
		}

		return nextStep;
	}, [activeStepKey, steps]);

	const previousStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let previousStepKey = activeStepKey - 1;
		let previousStep;

		while (previousStepKey > 0) {
			const step: SidebarConfigItem | undefined = steps?.find(
				// eslint-disable-next-line no-loop-func
				(step) => step.key === previousStepKey
			);

			if (step && !step.subitems) {
				previousStep = step;
				break;
			}

			--previousStepKey;
		}

		return previousStep;
	}, [activeStepKey, steps]);

	const activeMainStepArrayIndex = useMemo(() => {
		return sideBarConfig?.findIndex((step) => {
			if (activeMainStep?.id) {
				return activeMainStep.id === step?.id;
			} else {
				return activeMainStep?.key === step?.key;
			}
		});
	}, [sideBarConfig, activeMainStep]);

	const activeSubStepArrayIndex = useMemo(() => {
		return activeMainStep?.subitems?.findIndex((subStep) => activeSubStep?.id === subStep?.id);
	}, [activeSubStep, activeMainStep]);

	useEffect(() => {
		// Personal Details is locked once the investor moves past it (needed for Docs generation).
		const previousIsPersonalDetails = !!previousStep?.route?.includes(
			INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS
		);
		const currentIsPersonalDetails = pathname.includes(
			INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS
		);
		const wouldEscapeToPersonalDetails = previousIsPersonalDetails && !currentIsPersonalDetails;

		setIsBackDisabled(
			!activeStepKey ||
				activeStepKey === 1 ||
				activeStepKey > DOCUMENTS_STEP_KEY ||
				wouldEscapeToPersonalDetails
		);
	}, [activeStepKey, previousStep, pathname]);

	const stepsRef = useRef(steps);
	useEffect(() => {
		stepsRef.current = steps;
	}, [steps]);

	const recordStepAndNavigate = async (route: string) => {
		const stepId = stepsRef.current?.find((step) => step.route === route)?.id;
		if (!stepId) {
			return;
		}
		await updateInvestorCompletionStepRequest
			.fetch({ step: stepId as PatchShareholderInvestorCompletionStepDto['step'] })
			.concludeWithPromise();
		await getInvestorDataRequest.refetch();
		navigate(`${leaveFlowPath}${route}`);
	};

	// Form steps must be able to fail instantly, so the minimum-duration loader only wraps a
	// navigation that follows a step with nothing to save, and every Back.
	const saveThenNavigate = async (
		intent: WizardNavigationIntent,
		route: string | undefined,
		delay: 'always' | 'when-skipped' | 'never'
	) => {
		if (!route) {
			return;
		}
		const result = await stepRegistry.saveActiveStep(intent);
		if (!result.ok) {
			return;
		}
		if (delay === 'always' || (delay === 'when-skipped' && result.skipped)) {
			await startDelay(() => recordStepAndNavigate(route));
			return;
		}
		await recordStepAndNavigate(route);
	};

	useEffect(() => {
		const checkInvestorStep = () => {
			if (!steps) {
				return;
			}
			if (!getInvestorDataRequest.data || getInvestorDataRequest.isLoading) {
				return;
			}

			const currentCompletionStepId = getInvestorDataRequest.data?.completionStep;
			if (
				!currentCompletionStepId ||
				activeMainStep?.id === currentCompletionStepId ||
				activeSubStep?.id === currentCompletionStepId
			) {
				return;
			}

			// When the investor's leave workflow is COMPLETED, additional signatures
			// (PIV / witness / representative) may still be pending. Allow them to remain
			// on /contracts so they can sign for those roles.
			if (
				currentCompletionStepId === InvestmentWorkflowCompletionStep.COMPLETED &&
				pathname.includes(INVESTMENT_LEAVE_ROUTES.CONTRACTS)
			) {
				return;
			}

			const currentCompletionStepRoute = steps?.find(
				(step) => step?.id === currentCompletionStepId
			)?.route;
			if (!currentCompletionStepRoute) {
				const firstStepRoute = steps?.find(
					(step) => step?.id === InvestmentWorkflowCompletionStep.PERSONAL_INFORMATION
				)?.route;

				// The workflow sits on a step this programme's sidebar has no entry for, usually
				// because settings changed midway. Move the investor to the first step and let
				// their next Continue write the completion step — never write it from here, where
				// a config query that lands late would reset real progress.
				if (firstStepRoute && !pathname.includes(firstStepRoute)) {
					navigate(`${leaveFlowPath}${firstStepRoute}`);
				}
				return;
			}
			navigate(`${leaveFlowPath}${currentCompletionStepRoute}`);
		};
		checkInvestorStep();
	}, [pathname, steps, isPIVInvestment, getInvestorDataRequest.data]);

	const isLastStep = activeStepKey === SIDEBAR_MAX_ITEMS;

	const onNextStep = useCallback(async () => {
		if (isLastStep) {
			await startDelay(async () => {
				if (investmentDetails) {
					const pathName = ROUTES.VALUATIONS.replace(
						':investorId',
						`${investmentDetails.investorId}`
					);
					navigate(pathName);
					return;
				}
				navigate({
					pathname: ROUTES.DASHBOARD,
					search: createSearchParams({
						mipId: mipId || '',
					}).toString(),
				});
			});
			return;
		}

		await saveThenNavigate('next', nextStep?.route, 'when-skipped');
	}, [investmentDetails, isLastStep, activeSubStep, nextStep, activeMainStep]);

	const onPreviousStep = async () => {
		await saveThenNavigate('back', previousStep?.route, 'always');
	};

	const onSidebarNavigation = async (stepKey: number) => {
		const stepData = steps?.find((step) => step?.key === stepKey);
		if (!stepData?.id) {
			return;
		}
		await saveThenNavigate('jump', stepData.route, 'never');
	};

	const isLoading =
		isDelaying ||
		isLoadingState ||
		getInvestorDataRequest.isLoading ||
		updateInvestorCompletionStepRequest.isLoading ||
		getInvestorCustomDetailsRequest.isLoading ||
		isKycSettingLoading;

	const sideBarItems = useMemo((): PrimarySideListV2Props['sideListItems'] | undefined => {
		const isLowerThanContractsStep =
			activeMainStep?.key != null && activeMainStep?.key < DOCUMENTS_STEP_KEY;
		const onDocsStep = activeMainStep?.key === DOCUMENTS_STEP_KEY;
		// Documents depends on Personal Details, not KYC — so from Docs we let
		// the investor jump back into KYC to resolve rejections on unfinished investments
		const isKycSection = (route: string) => route === INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE;

		return sideBarConfig?.map((item) => {
			const sectionUnlocked =
				isLowerThanContractsStep || (onDocsStep && isKycSection(item.route));
			return {
				key: item.key as number,
				title: item.title as string,
				allowNavigation:
					isLowerThanContractsStep &&
					!item?.subitems?.length &&
					!!item?.id &&
					!!item?.key &&
					!!activeStepKey
						? item?.key < activeStepKey
						: false,
				allowExpandByClick:
					sectionUnlocked &&
					!!item?.subitems?.length &&
					activeMainStep?.key !== item?.key,
				subItems: item?.subitems?.map((subItem) => ({
					key: subItem?.key as number,
					title: subItem?.title as string,
					allowNavigation: sectionUnlocked && !!subItem?.key && !!activeStepKey,
				})),
			};
		});
	}, [sideBarConfig, activeMainStep, activeStepKey]);

	const redirectLink = useMemo(() => {
		return investorId
			? ROUTES.VALUATIONS.replace(':investorId', `${investorId}`)
			: ROUTES.DASHBOARD.toString();
	}, [investorId]);

	useEffect(() => {
		if (activeSubStep?.id === 'KYC_INSTRUCTIONS') {
			setIsContinueDisabled(false);
		}
	}, [activeStepKey, activeSubStep]);

	return (
		<Layout>
			{isLoading ? <InlineLoader /> : null}
			<investmentJoinAndAllocationContext.Provider
				value={{
					...({} as ReturnType<typeof useInvestmentJoinAndAllocationController>),
					setIsContinueDisabled,
					setIsBackDisabled,
					setIsLoading: setIsLoadingState,
					stepRegistry,
					confirmsDeclarationsBeforeContracts: false,
					isPIVInvestment,
					dataBackup,
					updateBackupData,
					removeBackupData,
					investmentDetails,
					kycSetting,
				}}
			>
				<WizardStepRegistryProvider registry={stepRegistry}>
					<MainHeaderBar
						title={activeMainStep?.title}
						activeMainStepIndex={activeMainStepArrayIndex}
						activeSubStepIndex={activeSubStepArrayIndex}
						sidebarItemsCount={sideBarConfig?.length}
						onPrevStep={onPreviousStep}
						isPrevDisabled={isBackDisabled}
						onNextStep={onNextStep}
						isNextDisabled={isContinueDisabled}
						isLastStep={isLastStep}
						redirectLink={redirectLink}
					/>

					<MainLayout>
						<PrimarySideListV2
							sideListItems={sideBarItems ?? []}
							activeKey={activeStepKey}
							onActiveChange={onSidebarNavigation}
						/>

						<Content>
							{activeSubStep?.id !== 'KYC_INSTRUCTIONS' && (
								<InvestmentHeader>
									<Title order={5}>
										{activeSubStep?.title ?? activeMainStep?.title}
									</Title>
									<div id={INVESTMENT_PORTAL.SECTION_HEADER} />
								</InvestmentHeader>
							)}

							<InvestmentContentWrapper>
								<SentryRoutes>
									<Route
										path={`${INVESTMENT_LEAVE_ROUTES.PERSONAL_DETAILS}/*`}
										element={
											<InvestmentLeavePersonalDetails
												setIsContinueDisabled={setIsContinueDisabled}
												setIsLoading={setIsLoadingState}
											/>
										}
									/>
									<Route
										path={`${INVESTMENT_LEAVE_ROUTES.CHECKS_COMPLETE}/*`}
										element={
											<InvestmentLeaveChecksComplete
												setIsContinueDisabled={setIsContinueDisabled}
												isPIVInvestment={isPIVInvestment}
												investmentDetails={investmentDetails}
												kycSetting={kycSetting}
											/>
										}
									/>
									<Route
										path={INVESTMENT_LEAVE_ROUTES.CONTRACTS}
										element={
											<NoSaveStep>
												<InvestmentLeaveContracts
													investorId={investorId}
													setIsContinueDisabled={setIsContinueDisabled}
												/>
											</NoSaveStep>
										}
									/>
									<Route
										path={INVESTMENT_LEAVE_ROUTES.COMPLETED}
										element={
											<NoSaveStep>
												<InvestmentLeaveCompleted
													setIsContinueDisabled={setIsContinueDisabled}
												/>
											</NoSaveStep>
										}
									/>
								</SentryRoutes>
							</InvestmentContentWrapper>
						</Content>
					</MainLayout>
				</WizardStepRegistryProvider>
			</investmentJoinAndAllocationContext.Provider>
		</Layout>
	);
};
